import { DATE_FORMATS } from "apps/portal/Portal/common/constants";
import { DEFAULT_DATE_FORMAT } from "apps/portal/Portal/common/UserPreferredFormatDate/constants";
import { format, formatISO, parseISO } from "date-fns";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import plur from "plur";

export const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24; // 86400000
/**
 * If no date format override is provided, returns the standard long date format for a date given.
 * Or else it returns the date in the specified format.
 *
 * By default uses the long "MMM d, yyyy" if no dateFormat is provided.
 */
export const toStandardFormat = (
  maybeDate: Date | string,
  dateFormatOverride?: string,
): string => {
  const date = typeof maybeDate === "string" ? parseISO(maybeDate) : maybeDate;
  // In case date is invalid and format fails, return an empty string
  try {
    return format(date, dateFormatOverride || DATE_FORMATS[DEFAULT_DATE_FORMAT].long);
  } catch {
    return "";
  }
};
export const toLongMonth = (maybeDate: Date | string): string => {
  const date = typeof maybeDate === "string" ? parseISO(maybeDate) : maybeDate;
  return format(date, "MMMM d, yyyy");
};
export const toISODate = (date: Date): string => format(date, "yyyy-MM-dd");
/**
 * Returns the standard short date format for a date given
 */
export const toShortDate = (date: Date): string =>
  format(date, DATE_FORMATS[DEFAULT_DATE_FORMAT].short);
export const toStandardFormatOrDashes = (
  date: Date | undefined | null,
  dateFormat?: string,
): string => (date ? toStandardFormat(date, dateFormat) : "--");
export const toQuarter = (date: Date): string => format(date, "QQQ yyyy");
export const toShortQuarter = (date: Date): string => format(date, "QQQ");

export const formatMonths = (totalMonths: number): string => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return `${years && `${years} ${plur("year", years)}, `}${months} ${plur("month", months)}`;
};

export const RAW_QUARTER_TO_PERIOD_MAP = {
  1: "first_quarter",
  2: "second_quarter",
  3: "third_quarter",
  4: "fourth_quarter",
};

export function isValidQuarter(value: number): value is keyof typeof RAW_QUARTER_TO_PERIOD_MAP {
  return value in RAW_QUARTER_TO_PERIOD_MAP;
}

export const getTimeAsISO = (time: string) => {
  return formatISO(new Date(`${format(new Date(), "MM/dd/yyyy")} ${time}`), {
    representation: "time",
  });
};

export function isValidDate(value: string | undefined | null) {
  try {
    if (!value) {
      return false;
    }
    toISODate(parseISO(value));
    return true;
  } catch (e) {
    return false;
  }
}

export const toFormattedDateWithTime = (dateString?: string, isoFormat?: boolean) => {
  if (!dateString) {
    return null;
  }

  const parsedDate = new Date(Date.parse(dateString));
  if (isoFormat) {
    return parsedDate.toISOString();
  }
  return parsedDate.toLocaleString();
};

/**
 * @param dateTime - Date and time in the format of "YYYY-MM-DD HH:MM:SS.SSS -0700"
 * @returns - Time in the format of "HH:MM AM/PM"
 */
export const dateTimeToLocalizedTime = (dateTime: string) => {
  const jsDateTime = dateTime.replace(" ", "T").replace(" ", "");
  const dateObject = new Date(jsDateTime);

  // Check for "Invalid Date"
  if (isNaN(dateObject.getTime())) {
    throw new Error("Invalid Date");
  }

  // Format the Date object to local time in 12-hour format
  return dateObject.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * @param dateTime - Date and time in the format of "YYYY-MM-DD HH:MM:SS.SSS -0700"
 * @returns - Time in the format of "mm/dd/YYYY"
 */
export const dateTimeToLocalizedDate = (dateTime: string) => {
  const jsDateTime = dateTime.replace(" ", "T").replace(" ", "");
  const dateObject = new Date(jsDateTime);

  // Check for "Invalid Date"
  if (isNaN(dateObject.getTime())) {
    throw new Error("Invalid Date");
  }

  // Format the Date object to local date format
  return dateObject.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * @param dateTime - Date object or date string in the format of "YYYY-MM-DD HH:MM:SS.SSS+00:00"
 * @param timeZone - Optional Timezone ["US/Pacific", "US/Hawaii"...] will fall back on toStandardFormat behavior
 * when not provided.
 * @return If no date format override is provided, returns the standard long date format for a date given.
 * Or else it returns the date in the specified format.
 */
export const toZonedDate = (
  dateTime: Date | string,
  timeZone?: string,
  dateFormatOverride?: string,
) => {
  const zonedDate = timeZone ? utcToZonedTime(dateTime, timeZone) : dateTime;

  return toStandardFormat(zonedDate, dateFormatOverride);
};

// Example output format: 'November 27, 2023 at 2:34 PM'
export const formatFullDateAndTime = (dateTime: string, includeAt = true) => {
  const date = parseISO(dateTime);
  const formatString = includeAt ? "MMMM d, yyyy 'at' h:mm a" : "MMMM d, yyyy h:mm a";
  return format(date, formatString);
};
